import { useCallback, useMemo, useState } from 'react';
import { useAppStore } from '@/store/appStore';
import { useProgressStore, selectIsProcessing } from '@/store/progressStore';
import { calculateMaxRejectionLevel } from '@/logic/adjustments';
import { WEIGHT } from '@/logic/weights';
import { isCoordinate } from '@/utils/fileParser';
import type {
  SolverOperationParams,
  SolverOperationResult,
  TransformationSolution,
} from '@/types';
import { useSolverWorker } from './useSolverWorker';
import { useBlunderCache, type DetectionOperation } from './useBlunderCache';

const store = useAppStore.getState;

const SCALE_ERROR = 'Scale must be a finite positive number.';

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError';

const hasFiniteSolution = (result: TransformationSolution): boolean =>
  [
    result.a,
    result.b,
    result.c,
    result.d,
    result.scale,
    result.rotation,
    result.rmsN,
    result.rmsE,
    result.rmsTotal,
  ].every(Number.isFinite) &&
  result.residuals.every((r) =>
    [r.vn, r.ve, r.total].every((v) => v === null || Number.isFinite(v))
  );

const isValidScaleText = (text: string): boolean =>
  !text || (isCoordinate(text) && Number(text) > 0);

/** The user's scale setting as a held scale, or null to estimate it. */
const parseScale = (text: string): number | null =>
  text ? Number(text) : null;

const hasInvalidCoordinates = (): boolean =>
  store().pointPairs.some((p) =>
    [p.n1, p.e1, p.n2, p.e2].some(
      (value) => value !== '' && !isCoordinate(value)
    )
  );

/**
 * Owns asynchronous analysis; only complete results are committed to the store.
 *
 * Handlers read store state when invoked rather than subscribing to it, so the
 * returned object only changes when `operationError` or the busy flag does.
 * Store actions never navigate; detection handlers explicitly open Adjust.
 */
export const useAnalysis = () => {
  const { run, cancel } = useSolverWorker();
  const isProcessing = useProgressStore(selectIsProcessing);
  const cache = useBlunderCache();
  const [operationError, setOperationError] = useState<string | null>(null);

  const reportOperationError = useCallback((error: unknown) => {
    if (isAbortError(error)) return;
    setOperationError(
      error instanceof Error
        ? error.message
        : 'Calculation failed. Please retry.'
    );
  }, []);

  const validateInputs = useCallback(() => {
    if (!hasInvalidCoordinates()) return true;
    setOperationError(
      'Correct invalid coordinates in the point table before analysis.'
    );
    return false;
  }, []);

  /**
   * Detection trials hold the same scale a solve or apply would use, so the
   * recommendations describe the fit the user will actually accept.
   */
  const detectionScale = useCallback((): number | null | undefined => {
    const { customScale } = store();
    if (isValidScaleText(customScale)) return parseScale(customScale);
    setOperationError(SCALE_ERROR);
    return undefined;
  }, []);

  const runSolve = useCallback(
    async (scaleOverride?: string, rejectedIds?: number[], preview = false) => {
      const snapshot = store();
      setOperationError(null);
      const scaleText = scaleOverride ?? snapshot.customScale;
      if (!isValidScaleText(scaleText)) {
        setOperationError(SCALE_ERROR);
        return;
      }
      if (!validateInputs()) return;
      if (rejectedIds && !rejectedIds.length) return;
      if (
        rejectedIds &&
        ((snapshot.combinatorialStale && snapshot.blunderResults) ||
          (snapshot.dataSnoopingStale && snapshot.dataSnoopingResults))
      ) {
        setOperationError(
          'Re-run the blunder check before previewing or applying outdated recommendations.'
        );
        return;
      }
      const scale = parseScale(scaleText);
      const points = rejectedIds
        ? snapshot.pointPairs.map((p) =>
            rejectedIds.includes(p.id) ? { ...p, weight: WEIGHT.free } : p
          )
        : snapshot.pointPairs;
      snapshot.clearTrialPreview();
      try {
        const result = await run('solve', points, { fixedScale: scale });
        if (
          store().pointPairs !== snapshot.pointPairs ||
          store().inputRevision !== snapshot.inputRevision ||
          store().customScale !== snapshot.customScale
        )
          return;
        if (result.error) {
          setOperationError(result.error);
          return;
        }
        if (!hasFiniteSolution(result)) {
          setOperationError(
            'The solution is not finite. Check coordinate magnitudes and control geometry.'
          );
          return;
        }
        if (preview && rejectedIds) {
          // A new detection run invalidates the recommendation being previewed.
          if (
            store().blunderResults !== snapshot.blunderResults ||
            store().dataSnoopingResults !== snapshot.dataSnoopingResults
          )
            return;
          store().setTrialPreview({
            pointPairs: points,
            solution: result,
            rejectedIds,
            inputRevision: snapshot.inputRevision,
            appliedScale: scale,
          });
          return;
        }
        if (scaleOverride !== undefined) store().setCustomScale(scaleOverride);
        if (rejectedIds)
          store().applyRejections(
            rejectedIds,
            result,
            snapshot.inputRevision,
            scale
          );
        else store().commitSolution(result, snapshot.inputRevision, scale);
      } catch (error) {
        reportOperationError(error);
      }
    },
    [run, reportOperationError, validateInputs]
  );

  /**
   * One detection step: use a cached result for the current input or run the
   * worker, then commit only if the input is unchanged. Resolves `true` when
   * a result was committed and `false` when the step was cancelled, failed
   * (the error is surfaced), or became obsolete.
   */
  const runDetection = useCallback(
    async <K extends DetectionOperation>(
      operation: K,
      params: SolverOperationParams<K>,
      commit: (result: SolverOperationResult<K>) => void
    ): Promise<boolean> => {
      const { pointPairs, inputRevision } = store();
      const input = { pointPairs, inputRevision };
      const cached = cache.get(operation, params, input);
      if (cached) {
        commit(cached);
        return true;
      }
      try {
        const result = await run(operation, pointPairs, params);
        if (
          store().pointPairs !== pointPairs ||
          store().inputRevision !== inputRevision
        )
          return false;
        cache.set(operation, params, input, result);
        commit(result);
        return true;
      } catch (error) {
        reportOperationError(error);
        return false;
      }
    },
    [cache, run, reportOperationError]
  );

  const handleSolve = useCallback(
    (scaleOverride?: string, rejectedIds?: number[]) =>
      runSolve(scaleOverride, rejectedIds),
    [runSolve]
  );

  const handlePreviewRejections = useCallback(
    (rejectedIds: number[]) => runSolve(undefined, rejectedIds, true),
    [runSolve]
  );

  const handleRunBlunder = useCallback(async () => {
    setOperationError(null);
    if (!validateInputs()) return;
    const fixedScale = detectionScale();
    if (fixedScale === undefined) return;
    const { rejectionLevel, viewAllSolutions } = store();
    store().setActiveDetectionMethod('combinatorial');
    if (
      await runDetection(
        'blunder',
        { rejectionLevel, viewAll: viewAllSolutions, fixedScale },
        store().setBlunderResults
      )
    )
      store().setActiveTab('pairs');
  }, [detectionScale, runDetection, validateInputs]);

  const handleRunAllBlunder = useCallback(async () => {
    setOperationError(null);
    if (!validateInputs()) return;
    const fixedScale = detectionScale();
    if (fixedScale === undefined) return;
    const { runAllMaxLevel } = store();
    store().setActiveDetectionMethod('combinatorial');
    if (
      await runDetection(
        'blunderAll',
        { maxLevel: runAllMaxLevel, fixedScale },
        store().setBlunderResults
      )
    )
      store().setActiveTab('pairs');
  }, [detectionScale, runDetection, validateInputs]);

  const handleRunDataSnooping = useCallback(async () => {
    setOperationError(null);
    if (!validateInputs()) return;
    const fixedScale = detectionScale();
    if (fixedScale === undefined) return;
    const { selectedAlpha } = store();
    store().setActiveDetectionMethod('dataSnooping');
    if (
      await runDetection(
        'dataSnooping',
        { alpha: selectedAlpha, fixedScale },
        store().setDataSnoopingResults
      )
    )
      store().setActiveTab('pairs');
  }, [detectionScale, runDetection, validateInputs]);

  /**
   * Unified check: run both methods at sensible defaults, then cross-check.
   * Data snooping starts only after the combinatorial step committed. A
   * cancelled, failed (error shown), or obsolete combinatorial step stops the
   * check, so a partial run never presents a one-sided result as a cross-check.
   */
  const handleCheckForBlunders = useCallback(async () => {
    setOperationError(null);
    if (!validateInputs()) return;
    const fixedScale = detectionScale();
    if (fixedScale === undefined) return;
    const { pointPairs, selectedAlpha } = store();
    store().setActiveTab('pairs');
    const maxLevel = Math.min(
      3,
      Math.max(1, calculateMaxRejectionLevel(pointPairs))
    );
    const combinatorialDone = await runDetection(
      'blunderAll',
      { maxLevel, fixedScale },
      store().setBlunderResults
    );
    if (!combinatorialDone) return;
    // Both detail sections should be visible after a check
    store().setActiveDetectionMethod(null);
    await runDetection(
      'dataSnooping',
      { alpha: selectedAlpha, fixedScale },
      store().setDataSnoopingResults
    );
  }, [detectionScale, runDetection, validateInputs]);

  return useMemo(
    () => ({
      isProcessing,
      cancel,
      clearCache: cache.clear,
      operationError,
      setOperationError,
      handleSolve,
      handlePreviewRejections,
      handleRunBlunder,
      handleRunAllBlunder,
      handleCheckForBlunders,
      handleRunDataSnooping,
    }),
    [
      isProcessing,
      cancel,
      cache,
      operationError,
      handleSolve,
      handlePreviewRejections,
      handleRunBlunder,
      handleRunAllBlunder,
      handleCheckForBlunders,
      handleRunDataSnooping,
    ]
  );
};
